import { AnalyticsBrowser } from '@segment/analytics-next';
import getEnv from '../env';
import { User } from './types/User';
import useABTestStore from '../stores/abTestStore';
import { cachedSessionStorage } from './cachedSessionStorage';
import useFormStore from '../features/Registration/useFormStore';

export const analytics = getEnv().segment_api_key
  ? AnalyticsBrowser.load({ writeKey: getEnv().segment_api_key })
  : null;

(window as any).analytics = analytics;

let prevPath: string | null = null;

function trackCurrentPage(pathname: string) {
  if (!getEnv().segment_api_key) {
    console.error('Segment API key missing, tracking disabled');
  }

  if (analytics && pathname !== prevPath) {
    const referrer = prevPath ?? document.referrer ?? '';
    prevPath = pathname;

    const campaign = cachedSessionStorage.getUTM();

    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(campaign)) {
      searchParams.set(`utm_${key}`, value as string);
    }

    let additionalProperties = {};
    if (location.hash) {
      additionalProperties = {
        hash: location.hash,
        pathname: location.pathname,
        url: location.href,
      };
    }

    // See: https://segment.com/docs/guides/how-to-guides/cross-channel-tracking/
    analytics.page(pathname, {
      abTests: useABTestStore.getState().ABTestsValues,
      search: searchParams.toString(),
      ...additionalProperties,
      referrer,
      context: {
        campaign,
      },
    });
  }
}

function trackEvent(eventName: string, payload: { [key: string]: any }) {
  // Check for user email in useFormStore
  const formStore = useFormStore.getState();
  const userEmail = formStore.units?.email || formStore.register?.email || '';

  if (analytics) {
    analytics.track(eventName, {
      ...payload,
      abTests: useABTestStore.getState().ABTestsValues,
      context: {
        ...(payload?.context || {}),
        email: userEmail,
      },
    });
  }
}

function mapUserToIdentifyPayload(user: User) {
  return {
    name: `${user.firstName} ${user.lastName}`,
    email: user.email,
    sizeOfPortfolio: user?.sizeOfPortfolio ?? undefined,
  };
}

function identifyUser(user: User) {
  if (analytics) {
    analytics.identify(user.id, mapUserToIdentifyPayload(user));
  }
}

function setAnonymousUserId(id: string) {
  if (analytics && getEnv().segment_api_key) {
    return analytics.setAnonymousId(id);
  }
}

export { trackCurrentPage, trackEvent, identifyUser, setAnonymousUserId, mapUserToIdentifyPayload };
